import type React from 'react';

import { isNil } from 'lodash-es';
import type { IconType } from 'react-icons';
import { onKeyUpDefault } from '../inputs/Input';
import Spinner from '../layout/Spinner';

interface ButtonProps {
  id: string;
  label: string;
  onClick: (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
  disabled?: boolean;
  loading?: boolean;
  outline?: boolean;
  accept?: boolean;
  reject?: boolean;
  custom?: string;
  small?: boolean;
  icon?: IconType;
  className?: string;
  ref?: any;
  collapseToIcon?: string;
}

const Button: React.FC<ButtonProps> = ({
  id,
  label,
  onClick,
  disabled,
  loading,
  outline,
  accept,
  reject,
  small,
  icon: Icon,
  className,
  ref,
  custom,
  collapseToIcon,
}) => {
  let bgColor = 'bg-sky-600';
  if (outline) {
    bgColor = 'bg-slate-50';
  } else if (accept) {
    bgColor = 'bg-green-500';
  } else if (reject) {
    bgColor = 'bg-red-600';
  } else if (!isNil(custom)) {
    if (custom === 'whatsapp') {
      bgColor = 'bg-slate-50';
    } else if (custom === 'sms') {
      bgColor = 'bg-slate-50';
    } else if (custom === 'outcancel') {
      bgColor = 'bg-slate-50';
    } else if (custom === 'white') {
      bgColor = 'bg-white';
    } else if (custom === 'tier-selected') {
      bgColor = 'bg-white';
    } else if (custom === 'tier-unselected') {
      bgColor = 'bg-sky-600';
    }
  }

  let borderColor = 'border-sky-600';
  if (outline) {
    borderColor = 'border-slate-500';
  } else if (accept) {
    borderColor = 'border-green-500';
  } else if (reject) {
    borderColor = 'border-red-600';
  } else if (!isNil(custom)) {
    if (custom === 'whatsapp') {
      borderColor = 'border-[#25d366]';
    } else if (custom === 'sms') {
      borderColor = 'border-sky-600';
    } else if (custom === 'outcancel') {
      borderColor = 'border-red-600';
    } else if (custom === 'white') {
      borderColor = 'border-slate-500';
    } else if (custom === 'tier-selected') {
      borderColor = 'border-white';
    } else if (custom === 'tier-unselected') {
      borderColor = 'border-sky-600';
    }
  }

  let textColor = 'text-white';
  if (outline) {
    textColor = 'text-slate-800';
  } else if (!isNil(custom)) {
    if (custom === 'whatsapp') {
      textColor = 'text-[#25d366]';
    } else if (custom === 'sms') {
      textColor = 'text-sky-600';
    } else if (custom === 'outcancel') {
      textColor = 'text-red-600';
    } else if (custom === 'white') {
      textColor = 'text-slate-800';
    } else if (custom === 'tier-selected') {
      textColor = 'text-sky-600';
    } else if (custom === 'tier-unselected') {
      textColor = 'text-white';
    }
  }

  return (
    <button
      onClick={(e) => {
        onClick(e);
      }}
      onKeyDown={(e) => {
        onClick(e);
      }}
      onKeyUp={onKeyUpDefault}
      ref={ref}
      disabled={disabled}
      data-test-id={`${id}_inner`}
      data-umami-event={`${id}-selected`}
      type="button"
      aria-label={label}
      className={`${className} relative size-full rounded-lg drop-shadow-sm text-base border font-bold transition hover:brightness-125 disabled:cursor-not-allowed disabled:opacity-25 ${bgColor} ${borderColor} ${textColor} ${
        small ? 'py-1 min-h-10' : 'py-3 min-h-14'
      }`}
    >
      {loading ? (
        <div className="flex flex-col">
          <div className="flex w-full flex-col items-center">
            <Spinner color="fill-sky-600" />
          </div>
        </div>
      ) : (
        <span className="line-clamp-1 flex min-w-[48px] flex-row items-center justify-center gap-2 overflow-hidden text-ellipsis break-all px-2">
          {Icon && <Icon size={24} />}
          <span
            className={`${!isNil(collapseToIcon) && `hidden ${collapseToIcon}:block`}`}
          >
            {label}
          </span>
        </span>
      )}
    </button>
  );
};

export default Button;
